/**
 * Referral Tier Configuration — Single Source of Truth
 * 
 * Shared between:
 *   - Frontend: WaitlistPage.tsx (tier ladder display)
 *   - Frontend: referral-tiers.ts (utility functions)
 *   - Backend:  waitlist-referral.service.ts (email templates)
 * 
 * ⚠️ If you change tiers here, the backend must also be updated.
 *    (Until we have a shared package, the backend mirrors this file.)
 */

export interface ReferralTier {
    id: string;
    label: string;
    count: number;
    reward: string;
    color: string;
}

export const REFERRAL_TIERS: ReferralTier[] = [
    { id: 'pioneer',     count: 1,  label: 'Pioneer',         color: '#34d399', reward: 'Early access. 2 weeks before public wave' },
    { id: 'accelerator', count: 3,  label: 'Accelerator',     color: '#00E5FF', reward: 'Jump 500 spots in the queue instantly' },
    { id: 'amplifier',   count: 7,  label: 'Amplifier',       color: '#f97316', reward: '+2,000 spots + Beta Tester badge' },
    { id: 'vanguard',    count: 15, label: 'Vanguard',        color: '#a78bfa', reward: 'Guaranteed access on Genesis Day' },
    { id: 'founding',    count: 30, label: 'Founding Member', color: '#e879f9', reward: 'Founding badge (lifetime) + 1 month Pro free' },
];

/**
 * Generate a deterministic referral code from an email address.
 * Uses FNV-1a hash for collision resistance. Must match backend implementation exactly.
 */
export function generateReferralCode(email: string): string {
    let hash = 2166136261;
    for (let i = 0; i < email.length; i++) {
        hash ^= email.charCodeAt(i);
        hash = (hash * 16777619) >>> 0;
    }
    return hash.toString(36).toUpperCase().padStart(8, '0').substring(0, 8);
}

/**
 * Find which tier a user has reached based on their referral count.
 * Returns null if no tier reached yet (0 referrals).
 */
export function getCurrentTier(referralCount: number): ReferralTier | null {
    let current: ReferralTier | null = null;
    for (const tier of REFERRAL_TIERS) {
        if (referralCount >= tier.count) {
            current = tier;
        }
    }
    return current;
}

/**
 * Find the next tier a user should aim for.
 * Returns null if user has reached the highest tier.
 */
export function getNextTier(referralCount: number): ReferralTier | null {
    for (const tier of REFERRAL_TIERS) {
        if (referralCount < tier.count) {
            return tier;
        }
    }
    return null;
}
